import { ProBadge } from "@/components/ProBadge";
import { useState, useEffect, useRef } from "react";
import { BrandMark } from "@/components/BrandMark";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";

const PROMPTS = [
  "What should I eat before training?",
  "I only slept 5 hours — what now?",
  "How do I improve my Healthspan fastest?",
];

export default function CoachChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/coach/messages").then(({ data }) => setMessages(data.messages)); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/coach/message", { text: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I hit a snag — try me again in a moment." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="clay flex flex-col h-[calc(100dvh-290px)] sm:h-[calc(100vh-190px)] min-h-[420px] sm:min-h-[520px]" data-testid="coach-chat">
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-border">
        <BrandMark size={36} />
        <div>
          <div className="font-display font-medium flex items-center gap-2">Ikavio Coach <ProBadge testId="pro-badge-coach" /></div>
          <div className="text-xs text-sage flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sage" /> Online & context-aware
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin px-6 py-5 space-y-4">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] px-4 py-3 text-[15px] leading-relaxed ${
                m.role === "user" ? "bg-brand text-white rounded-3xl rounded-br-md" : "bg-secondary text-ink rounded-3xl rounded-bl-md"}`}>
                {m.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {sending && (
          <div className="flex justify-start">
            <div className="bg-secondary rounded-3xl rounded-bl-md px-4 py-3.5 flex gap-1.5">
              {[0, 1, 2].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-muted2 typing-dot" style={{ animationDelay: `${d * 0.2}s` }} />)}
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-6 pb-2 flex flex-wrap gap-2">
          {PROMPTS.map((p) => (
            <button key={p} data-testid="coach-suggested-prompt" onClick={() => send(p)}
              className="text-xs bg-surface border border-border rounded-full px-3 py-1.5 hover:bg-secondary transition-colors">
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-border">
        <div className="flex items-end gap-2 bg-secondary/60 rounded-2xl p-2 pl-4">
          <textarea
            data-testid="coach-input"
            value={input} rows={1}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask your coach anything…"
            className="flex-1 bg-transparent resize-none outline-none text-[15px] py-2 max-h-28"
          />
          <button data-testid="coach-send-btn" onClick={() => send()} disabled={sending || !input.trim()}
            className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center hover:bg-ink transition-colors disabled:opacity-40 shrink-0">
            <PaperPlaneRight size={17} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
